import { z } from 'zod'
import {
  skillInstalledSchema,
  skillInvocationSchema,
  scheduledSkillRunSchema,
  type SkillInstalled,
  type SkillInvocation,
  type ScheduledSkillRun,
} from '@mindkeep/shared'
import { apiRequest, buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'
import type { ChatMessage } from '@/features/chat/chat-api'

const skillsListSchema = z.object({ skills: z.array(skillInstalledSchema) })
const invocationsListSchema = z.object({ invocations: z.array(skillInvocationSchema) })
const builtinSkillDefinitionSchema = z.object({
  name: z.string(),
  description: z.string(),
  section: z.enum(['built_in', 'scheduled']),
  taskGroup: z.string().optional(),
  defaultEnabled: z.boolean().optional(),
  defaultCron: z.string().optional(),
  defaultScheduleLabel: z.string().optional(),
  examples: z.array(z.string()).optional(),
  model: z.string().optional(),
})
const skillCatalogSchema = z.object({
  built_in: z.array(builtinSkillDefinitionSchema),
  scheduled: z.array(z.object({
    definition: builtinSkillDefinitionSchema.nullable(),
    run: scheduledSkillRunSchema,
  })),
  custom: z.array(skillInstalledSchema),
})
const scheduledRunResultSchema = z.object({
  run: scheduledSkillRunSchema,
  result: z.unknown(),
})

export type BuiltinSkillDefinition = z.infer<typeof builtinSkillDefinitionSchema>
export type SkillCatalog = z.infer<typeof skillCatalogSchema>
export type { SkillInstalled, SkillInvocation, ScheduledSkillRun }

export function listInstalledSkills() {
  return apiRequest({ path: '/skills/installed', schema: skillsListSchema })
}

export function listAllSkills() {
  return apiRequest({ path: '/skills/all', schema: skillsListSchema })
}

export function getSkillCatalog() {
  return apiRequest({ path: '/skills/catalog', schema: skillCatalogSchema })
}

export function installSkill(body: { source_repo: string; skill_name?: string }) {
  return apiRequest({
    method: 'POST',
    path: '/skills/install',
    body,
    schema: skillInstalledSchema,
  })
}

export function toggleSkill(skillId: string, enabled: boolean) {
  return apiRequest({
    method: 'PATCH',
    path: `/skills/${skillId}`,
    body: { enabled },
    schema: skillInstalledSchema,
  })
}

export function updateScheduledSkillRun(
  id: string,
  body: {
    enabled?: boolean
    schedule_cron?: string
    schedule_label?: string
    timezone?: string
  },
) {
  return apiRequest({
    method: 'PATCH',
    path: `/skills/scheduled/${id}`,
    body,
    schema: scheduledSkillRunSchema,
  })
}

export function runScheduledSkill(id: string) {
  return apiRequest({
    method: 'POST',
    path: `/skills/scheduled/${id}/run`,
    schema: scheduledRunResultSchema,
  })
}

export function listSessionInvocations(sessionId: string) {
  return apiRequest({
    path: `/skills/invocations?session_id=${encodeURIComponent(sessionId)}`,
    schema: invocationsListSchema,
  })
}

// ---- Skill-Invocation Stream ----

export type SkillStreamEvent =
  | { type: 'user_message'; message: ChatMessage }
  | { type: 'skill_invoked'; skill_name: string; stub: boolean }
  | { type: 'token'; content: string }
  | { type: 'done'; message: ChatMessage }
  | { type: 'error'; message: string }

interface InvokeSkillStreamOptions {
  sessionId: string
  skillName: string
  operation?: string
  args?: string
  onEvent: (event: SkillStreamEvent) => void
  signal?: AbortSignal
}

export async function streamSkillInvocation(
  opts: InvokeSkillStreamOptions,
): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/skills/invoke`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({
      session_id: opts.sessionId,
      skill_name: opts.skillName,
      operation: opts.operation,
      args: opts.args,
    }),
    signal: opts.signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  if (!response.body) throw new Error('Keine Stream-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)

        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          try {
            const parsed = JSON.parse(payload) as SkillStreamEvent
            opts.onEvent(parsed)
          } catch (err) {
            console.error('SSE parse error', err, payload)
          }
        }

        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}
